import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';

interface EnhancedDropdownProps<T> {
  /**
   * Array of items.
   */
  items: T[];
  /**
   *  Default value or simple value.
   */
  defaultSelected: T[];
  /**
   *  Callback add to Cancel button.
   */
  onCancel?: () => void;
  /**
   *  Callback add to Apply button.
   */
  onApply?: (items: T[]) => void;
  /**
   * Validation Callback added to Apply button.
   */
  onValidateApply?: (selectedItems: T[]) => boolean;
}

export interface EnhancedDropdownReturn<T> {
  selectedItems: T[];
  setSelected: Dispatch<SetStateAction<T[]>>;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  searchText: string;
  filteredItems: T[];
  handleCancel: () => void;
  handleApply: () => void;
  handleOpenChange: (visible: boolean) => void;
  handleSelectAll: () => void;
  handleClearAll: () => void;
  handleToggleSelect: (item: T) => (event: CheckboxChangeEvent) => void;
  handleDeleteSelected: (item: T) => () => void;
  handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedCountText: string;
  selectedCount: number;
}

/**
 * Hook to use for EnhancedDropdown us it with EnhancedDropdownLayout
 * @params EnhancedDropdownProps<T>
 * @returns EnhancedDropdownReturn<T>
 */
export const useEnhancedDropdown = <T extends { text: string; id: string | number }>({
  items,
  onCancel,
  onApply,
  defaultSelected,
  onValidateApply,
}: EnhancedDropdownProps<T>): EnhancedDropdownReturn<T> => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<T[]>([]);
  const [searchText, setSearch] = useState('');

  const filteredItems = items.filter((item) => item.text.toLowerCase().includes(searchText.toLowerCase()));

  useEffect(() => {
    setSelected(defaultSelected ?? []);
  }, []);

  const handleCancel = (): void => {
    setIsOpen(false);
    setSelected(defaultSelected ?? []);
    onCancel && onCancel();
  };

  const handleApply = (): void => {
    if (onValidateApply) {
      const isValid = onValidateApply(selected);
      if (isValid !== true) {
        return;
      }
    }
    setIsOpen(false);
    onApply && onApply(selected);
  };

  const handleOpenChange = (visible: boolean): void => {
    if (!visible) {
      setSelected(defaultSelected ?? []);
    }

    setIsOpen(visible);
  };

  const handleSelectAll = (): void => {
    const missedElements = filteredItems.filter((item) => !selected.some((sel) => sel.id === item.id));
    if (missedElements.length) {
      setSelected((prevSelected) => [...prevSelected, ...missedElements]);
    }
  };

  const handleClearAll = (): void => {
    setSelected([]);
  };

  const handleToggleSelect = (item: T) => (event: CheckboxChangeEvent) => {
    setSelected((prevSelected) =>
      event.target.checked ? [...prevSelected, item] : prevSelected.filter((itm) => itm.id !== item.id)
    );
  };

  const handleDeleteSelected = (item: T) => (): void => {
    setSelected((prevSelected) => prevSelected.filter((itm) => itm.id !== item.id));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value);
  };

  const selectedCount = selected?.length || 0;
  const selectedCountText = `${selectedCount} ${selectedCount === items.length ? '(All)' : ''} Selected Items`;

  return {
    isOpen,
    selectedItems: selected,
    searchText,
    handleCancel,
    handleApply,
    handleOpenChange,
    handleSelectAll,
    handleClearAll,
    handleToggleSelect,
    handleDeleteSelected,
    handleSearch,
    filteredItems,
    selectedCount,
    setSelected,
    selectedCountText,
    setOpen: setIsOpen,
  };
};
